import React from "react";
import { Box, Card, Stack, Typography, Checkbox } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TransportOrderDto } from "../../../models/transport/transportOrder";
import { Base } from "../../../framework/common/base";

interface IOrderTypeProps {
    order: TransportOrderDto;
}

interface NormalizedSchema {
    type?: string;
    properties?: {
        [key: string]: {
            title?: string;
            type?: string;
            format?: string;
        };
    };
}

const formatValue = (value: unknown, type?: string, format?: string, schema?: NormalizedSchema): React.ReactNode => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "string") {
        if (format === "date") {
            return Base.dateStrToOriginalTimezoneDateStr(value);
        }
        if (format === "date-time") {
            return Base.dateStrToOriginalTimezoneDateTimeStr(value);
        }
    }

    switch (type?.toLowerCase()) {
        case "boolean":
            return <Checkbox checked={Boolean(value)} disabled size="small" />;
        case "object":
            if (value !== null && typeof value === "object") {
                const nestedSchema = schema?.properties || {};
                return (
                    <Stack spacing={1} sx={{ pl: 2, mt: 1 }}>
                        {Object.entries(value as Record<string, unknown>).map(([nestedKey, nestedValue]) => {
                            const nestedFieldSchema = nestedSchema[nestedKey] || {};
                            return (
                                <Box key={nestedKey}>
                                    <Typography variant="subtitle2" sx={{ fontSize: "0.875em" }}>
                                        {nestedFieldSchema.title || nestedKey}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatValue(nestedValue, nestedFieldSchema.type, nestedFieldSchema.format, nestedFieldSchema)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                );
            }
            return String(value);
        case "number":
            return typeof value === "number" ? value.toLocaleString() : String(value);
        default:
            return String(value);
    }
};

export const OrderType = ({ order }: IOrderTypeProps) => {
    const { t } = useTranslation();
    const formValues = JSON.parse(order.formValues || "{}");

    const normalizeSchema = (schema: any): NormalizedSchema => {
        try {
            return typeof schema === "string"
                ? (JSON.parse(schema) as NormalizedSchema)
                : (schema as NormalizedSchema) || { type: "object" };
        } catch {
            return { type: "object" };
        }
    };

    const schema = normalizeSchema(order.formData);
    const entries = Object.entries(schema.properties || {});

    if (entries.length === 0) {
        return null;
    }

    return (
        <>
            <Card variant="outlined"
                sx={{ m: 2, p: 2, bgcolor: "lightBlue.main" }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: "bold",
                        color: "primary.dark",
                        mb: 2
                    }}
                >
                    {t("transport.order.orderType")}: {order.formTitle}
                </Typography>
                <Stack spacing={2}>
                    {entries.map(([key, fieldSchema]) => {
                        const label = fieldSchema.title || key;
                        const value = formValues[key];

                        return (
                            <Box
                                key={key}
                            >
                                <Typography variant="subtitle2">
                                    {label}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {formatValue(value, fieldSchema.type, fieldSchema.format, fieldSchema)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            </Card>
        </>
    );
};
